import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { OrganizationUser, OrganizationUserInvitePayload } from '@/api/OrganizationUserModel'
import axios from 'axios'

const QUERY_KEY = 'organization-users'

export const useOrganizationUsers = (organizationId: string) => {
  return useQuery<OrganizationUser[]>({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<OrganizationUser[]>(
        `/v1/organizations/${organizationId}/users`
      )
      return res.data
    }
  })
}

export const useInviteOrganizationUser = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation<{}, Error, OrganizationUserInvitePayload>({
    mutationFn: async (payload: OrganizationUserInvitePayload) => {
      try {
        const res = await apiClient.post<{}>(`/v1/organizations/${organizationId}/users`, payload)

        await queryClient.fetchQuery({
          queryKey: [QUERY_KEY]
        })

        return res.data
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 409) {
            throw new Error('CONFLICT')
          }
        }
        throw error
      }
    }
  })
}

export const useOrganizationUserRoles = (organizationId: string) => {
  return useQuery<string[]>({
    queryKey: ['organization-roles'],
    gcTime: Infinity,
    queryFn: async () => {
      const res = await apiClient.get(`/v1/organizations/${organizationId}/roles`)
      return res.data
    }
  })
}

export const useUpdateUserRole = (organizationId: string, userId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { roleSlug: string }) => {
      const res = await apiClient.put<{}>(
        `/v1/organizations/${organizationId}/users/${userId}/roles`,
        payload
      )

      await queryClient.fetchQuery({
        queryKey: [QUERY_KEY]
      })

      return res.data
    }
  })
}

export const useDeleteUserRole = (organizationId: string, userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { roleSlug: string }) => {
      const res = await apiClient.delete<{}>(
        `/v1/organizations/${organizationId}/users/${userId}/roles`,
        {
          data: payload
        }
      )

      await queryClient.fetchQuery({
        queryKey: [QUERY_KEY]
      })

      return res.data
    }
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.delete<{}>(`/v1/user/${userId}`)

      await queryClient.fetchQuery({
        queryKey: [QUERY_KEY]
      })

      return res.data
    }
  })
}
